import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import {
  loadCart,
  saveCart,
  addLine,
  updateQuantity,
  removeLine,
  cartSubtotal,
  cartCount,
  cartLoad,
  type StoredCart,
} from '../lib/cart.ts';
import type { CartLine } from '../lib/types.ts';
import { useTenant } from './TenantContext.tsx';

interface CartState {
  lines: CartLine[];
  locationLocalId: string;
}

type CartAction =
  | { type: 'ADD'; line: CartLine }
  | { type: 'UPDATE_QTY'; lineId: string; delta: number }
  | { type: 'REMOVE'; lineId: string }
  | { type: 'CLEAR' }
  | { type: 'LOAD'; lines: CartLine[]; locationLocalId: string };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD':
      return { ...state, lines: addLine(state.lines, action.line) };
    case 'UPDATE_QTY':
      return { ...state, lines: updateQuantity(state.lines, action.lineId, action.delta) };
    case 'REMOVE':
      return { ...state, lines: removeLine(state.lines, action.lineId) };
    case 'CLEAR':
      return { ...state, lines: [] };
    case 'LOAD':
      return { lines: action.lines, locationLocalId: action.locationLocalId };
    default:
      return state;
  }
}

interface CartContextValue {
  lines: CartLine[];
  locationLocalId: string;
  subtotal: number;
  count: number;
  load: number;
  addLine: (line: CartLine) => void;
  updateQty: (lineId: string, delta: number) => void;
  removeLine: (lineId: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { slug, snapshot } = useTenant();
  const defaultLocation = snapshot.location.local_id;

  const [state, dispatch] = useReducer(cartReducer, {
    lines: [],
    locationLocalId: defaultLocation,
  });

  // Load from localStorage on mount
  useEffect(() => {
    const stored = loadCart(slug);
    if (stored) {
      dispatch({ type: 'LOAD', lines: stored.lines, locationLocalId: stored.location_local_id });
    }
  }, [slug]);

  // Persist to localStorage on change
  useEffect(() => {
    const stored: StoredCart = {
      location_local_id: state.locationLocalId,
      lines: state.lines,
      updated_at: Date.now(),
    };
    saveCart(slug, stored);
  }, [slug, state]);

  const add = useCallback((line: CartLine) => dispatch({ type: 'ADD', line }), []);
  const updateQty = useCallback(
    (lineId: string, delta: number) => dispatch({ type: 'UPDATE_QTY', lineId, delta }),
    [],
  );
  const remove = useCallback((lineId: string) => dispatch({ type: 'REMOVE', lineId }), []);
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  return (
    <CartContext.Provider
      value={{
        lines: state.lines,
        locationLocalId: state.locationLocalId,
        subtotal: cartSubtotal(state.lines),
        count: cartCount(state.lines),
        load: cartLoad(state.lines),
        addLine: add,
        updateQty,
        removeLine: remove,
        clearCart: clear,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
